import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertBanner, Spinner } from '@spark';
import { fetchLlmUsecaseInfo, fetchSummarizationEvaluation, updateLlmJudgeConfig } from '@/features/evaluation/api/summarizationApi';
import SummarizationOverview from './SummarizationOverview';
import SummarizationSamples from './SummarizationSamples';
import FilterBar from '../FilterBar';
import EvaluationEmptyState from '../EvaluationEmptyState';
import { TaskType } from '@/shared/domain/taskTypes';
import IncompleteTrainingAlert from '../IncompleteTrainingAlert';
import EvaluationErrorsAlert from '../EvaluationErrorsAlert';
import EvaluationProcessingState from '../EvaluationProcessingState';
import EvaluationViewTabs from '../EvaluationViewTabs';
import ModelDetailsDrawer from '@/features/trainings/components/ModelDetailsDrawer';
import { useAuth } from '@/features/auth/AuthContext';
import { TASK_NESTED_MODEL_DRAWER_BODY_CLASS } from '@/features/tasks/components/Task/taskDrawerLayout';

import { buildCompareOptions } from '../compareOptions';
import { useStableTeacherNameForCompare } from '../useStableTeacherNameForCompare';
import PerformanceTab from '../Performance/PerformanceTab';
import { useEvaluationResync } from '../../hooks/useEvaluationResync';
import { recalculationRunFromResponse } from '../../utils/finishEvaluationProgress';
import { isEvaluationErrorAnswer } from '../../utils/evaluationErrorAnswer';
import { createEvaluationQueryOrder } from '../../utils/evaluationQueryOrder';
import { isTrainingInProgress } from '../../utils/evaluationEmptyState';
import EvaluationTrainingInProgressState from '../EvaluationTrainingInProgressState';
import {
  DEFAULT_COST_CONFIG,
  DEFAULT_PERF_FILTERS,
} from '../Performance/data/performanceMetricsApi';

/** Fresh default filters for Performance tab (avoid mutating module export). */
function newPerformanceFiltersState() {
  return {
    ...DEFAULT_PERF_FILTERS,
    environments: DEFAULT_PERF_FILTERS.environments.map((e) => ({ ...e })),
  };
}

function errorOccurrenceFingerprint(errors) {
  if (!Array.isArray(errors) || errors.length === 0) return null;
  return errors.map((item) => [item?.error, Number(item?.number_of_occurrences) || 1]);
}

/** Build a comparable fingerprint of evaluation response for display-only fields. Skip state update when unchanged to avoid redundant re-renders during polling. */
function evaluationDisplayFingerprint(data) {
  if (!data) return '';
  const cards = data?.all_metrics?.summarization_overview?.score_cards || [];
  const judge = data?.teacher_model_judge_progress;
  return JSON.stringify({
    success: data.success,
    distillation_status: data.distillation_status,
    teacher_model_processing: data.teacher_model_processing,
    teacher_model_progress_completed: data.teacher_model_progress_completed,
    teacher_model_progress_total: data.teacher_model_progress_total,
    teacher_judge: judge
      ? {
        completed: judge.completed,
        total: judge.total,
        failed: judge.failed,
      }
      : null,
    cards: cards.map((c) => ({ type: c?.type, score: c?.score })),
    evaluation_errors: errorOccurrenceFingerprint(data.evaluation_errors),
    teacher_evaluation_errors: errorOccurrenceFingerprint(data.teacher_evaluation_errors),
  });
}

// Overview / Samples side-rail items (same layout as classification / NER
// evaluations and the Data Editor).
const EVAL_VIEW_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'samples', label: 'Samples' },
  // TODO: SHANI - add back in after real performance metrics are seeded
  // { id: 'performance', label: 'Performance' },
];

const fallbackClientId = "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";
const DEFAULT_MOCK_VERSION = 2.2;

const SummarizationEvaluation = ({
  currentUsecase,
  moveToScreen,
  evaluationProgress = {},
  setEvaluationProgress,
  evaluationResultsReady = {},
  realtimeResyncTick = 0,
  trainStatuses = {},
  embeddedInTaskDrawer = false,
  onRetrain = null,
}) => {
  const clientId = currentUsecase?.client_id || fallbackClientId;
  const llmUsecaseId = currentUsecase?._id;
  const { role } = useAuth();

  const [loading, setLoading] = useState(true);
  const [evaluationResult, setEvaluationResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview | samples
  const [modelDetailsOpen, setModelDetailsOpen] = useState(false);
  // Mirrors antd Alert `closable`: hides the failure banner until remount.
  const [failureAlertDismissed, setFailureAlertDismissed] = useState(false);

  useEffect(() => {
    if (!embeddedInTaskDrawer) return undefined;
    if (modelDetailsOpen) {
      document.body.classList.add(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
    } else {
      document.body.classList.remove(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
    }
    return () => document.body.classList.remove(TASK_NESTED_MODEL_DRAWER_BODY_CLASS);
  }, [modelDetailsOpen, embeddedInTaskDrawer]);

  const [usecaseInfo, setUsecaseInfo] = useState(currentUsecase || null);
  const [recalcPending, setRecalcPending] = useState(false);
  // { runId, version } of the recalculation this page started, from the trigger response.
  const [recalcRun, setRecalcRun] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const recalcStartedAtRef = useRef(null);

  // Initialize a stable version so we can fetch exactly once on mount.
  const [selectedVersion, setSelectedVersion] = useState(() => {
    const v = currentUsecase?.version;
    return v ?? DEFAULT_MOCK_VERSION;
  });

  // Lock the response to a specific benchmark row (recalc minor version) selected
  // from the Model Details drawer's Benchmarks tab. `null` means "use latest".
  const [selectedBenchmarkEventId, setSelectedBenchmarkEventId] = useState(null);

  const prevUsecaseIdRef = useRef(llmUsecaseId);
  const [performanceFilters, setPerformanceFilters] = useState(newPerformanceFiltersState);
  const [performanceCostConfig, setPerformanceCostConfig] = useState(() => ({
    ...DEFAULT_COST_CONFIG,
  }));

  useEffect(() => {
    if (prevUsecaseIdRef.current !== llmUsecaseId) {
      prevUsecaseIdRef.current = llmUsecaseId;
      setEvaluationResult(null);
      setLoading(true);
      setPerformanceFilters(newPerformanceFiltersState());
      setPerformanceCostConfig({ ...DEFAULT_COST_CONFIG });
    }
  }, [llmUsecaseId]);
  const versionOptions = evaluationResult?.versions || [selectedVersion];

  // Match non-summarization behavior: default to teacher compare only once
  // `teacherNameForCompare` is known (from /evaluation), not on stale usecase.teacher_model.
  const [currentToCompare, setCurrentToCompare] = useState(null);
  const userTouchedCompareRef = useRef(false);
  const showComparison = currentToCompare != null;

  const [queryOrder] = useState(createEvaluationQueryOrder);
  const loadEvaluation = useCallback(async (version, compareTarget, options = {}) => {
    const { background = false, benchmarkEventId } = options;
    if (!llmUsecaseId) return;
    // An answer that a newer load superseded is ignored.
    const isCurrent = queryOrder.start(background);
    if (!background) setLoading(true);
    try {
      const compare_with_file = compareTarget === 'file';
      const compare_with_version =
        typeof compareTarget === 'number' ? compareTarget : null;
      const comparison_mode = compareTarget != null;

      const res = await fetchSummarizationEvaluation({
        client_id: clientId,
        llm_usecase_id: llmUsecaseId,
        version,
        comparison_mode,
        compare_with_version,
        compare_with_file,
        selected_distillation_event_id:
          benchmarkEventId !== undefined ? benchmarkEventId : selectedBenchmarkEventId,
        task_type: currentUsecase?.task_type,
      }, background ? () => {} : undefined);
      // A failed background load keeps the current result, without a toast.
      if (!isCurrent() || (background && (!res || isEvaluationErrorAnswer(res)))) return;
      setEvaluationResult((prev) => {
        if (prev && evaluationDisplayFingerprint(prev) === evaluationDisplayFingerprint(res)) {
          return prev;
        }
        return res;
      });

      // Seed evaluationProgress with per-sub-task data persisted on the distillation event
      // so that after page refresh the UI shows all sub-task details (not just the latest one).
      // Skip when the evaluation is already terminal — seeding stale "evaluating" entries
      // would make wsInProgress true and show the processing overlay instead of results.
      const subTasks = res?.llm_judge_sub_tasks;
      const isTerminal = res?.distillation_status === 'completed' || res?.distillation_status === 'failed';
      if (subTasks && typeof subTasks === 'object' && llmUsecaseId && !isTerminal) {
        setEvaluationProgress?.((prev) => {
          const next = { ...prev };
          for (const [st, data] of Object.entries(subTasks)) {
            const v = data?.version ?? version;
            const key = `${llmUsecaseId}_${v}_${st}`;
            if (!prev[key]) {
              next[key] = {
                usecase_id: llmUsecaseId,
                distillation_event_id: res?.selected_distillation_event_id,
                version: v,
                sub_task_type: st,
                step_name: data?.step_name,
                status: data?.status || 'evaluating',
                current_step: data?.current_step,
                total_steps: data?.total_steps,
              };
            }
          }
          return next;
        });
      }

      // Normalize selected version into returned version list (one-time correction).
      const returnedVersions = res?.versions || [];
      if (Array.isArray(returnedVersions) && returnedVersions.length > 0) {
        if (!returnedVersions.includes(version)) {
          setSelectedVersion(Math.max(...returnedVersions));
        }
      }
    } finally {
      if (!background && isCurrent()) setLoading(false);
    }
  }, [clientId, llmUsecaseId, selectedBenchmarkEventId, queryOrder]);

  const lastAutoRefreshKeyRef = useRef(null);
  useEffect(() => {
    // Refresh exactly once when websocket signals evaluation results are ready.
    if (!llmUsecaseId || selectedVersion == null) return;
    const major = Math.floor(Number(selectedVersion));
    if (!Number.isFinite(major)) return;
    const signal = evaluationResultsReady?.[`${llmUsecaseId}_${major}`];
    if (!signal) return;
    const refreshKey = `${llmUsecaseId}_${major}:${signal?.ts}`;
    if (lastAutoRefreshKeyRef.current === refreshKey) return;
    lastAutoRefreshKeyRef.current = refreshKey;
    loadEvaluation(selectedVersion, currentToCompare);
    setRecalcPending(false);
  }, [evaluationResultsReady, llmUsecaseId, selectedVersion, currentToCompare, loadEvaluation]);

  useEvaluationResync({
    clientId,
    usecaseId: llmUsecaseId,
    realtimeResyncTick,
    reloadInBackground: () => {
      if (selectedVersion != null) loadEvaluation(selectedVersion, currentToCompare, { background: true });
    },
    evaluation: evaluationResult,
    evaluationProgress,
    setEvaluationProgress,
    pendingRun: recalcPending ? recalcRun : null,
  });

  // Disallow comparing a version to itself (SpecificAI vs the same SpecificAI version).
  // If the user changes either Select such that they're equal, clear Compare.
  useEffect(() => {
    if (selectedVersion == null) return;
    if (typeof currentToCompare !== 'number') return;

    const toMajor = Math.floor(Number(selectedVersion));
    const compareMajor = Math.floor(Number(currentToCompare));
    if (!Number.isFinite(toMajor) || !Number.isFinite(compareMajor)) return;

    if (toMajor === compareMajor) {
      setCurrentToCompare(null);
    }
  }, [selectedVersion, currentToCompare]);

  const teacherNameForCompare = useStableTeacherNameForCompare({
    evaluatedTeacherModelName: evaluationResult?.evaluated_teacher_model_name,
    teacherModelProcessing: evaluationResult?.teacher_model_processing,
    usecaseTeacherModel: currentUsecase?.teacher_model,
  });
  const teacherModelLabel = useMemo(
    () => teacherNameForCompare || 'Commercial model',
    [teacherNameForCompare]
  );
  const studentModelLabel = useMemo(() => {
    if (selectedVersion == null) return 'Specific AI';
    const v = Number(selectedVersion);
    return Number.isFinite(v) && v % 1 === 0 ? `Specific AI v${v}.0` : `Specific AI v${selectedVersion}`;
  }, [selectedVersion]);

  const refreshUsecaseInfo = useCallback(async () => {
    if (!llmUsecaseId) return;
    try {
      const res = await fetchLlmUsecaseInfo({ client_id: clientId, llm_usecase_id: llmUsecaseId });
      if (res?.llm_usecase) setUsecaseInfo(res.llm_usecase);
    } catch (e) {
      // best-effort; keep existing
    }
  }, [clientId, llmUsecaseId]);

  useEffect(() => {
    setUsecaseInfo(currentUsecase || null);
    refreshUsecaseInfo();
  }, [currentUsecase, refreshUsecaseInfo]);

  const persistJudgeConfig = useCallback(async (payload) => {
    if (!llmUsecaseId) return;
    await updateLlmJudgeConfig({
      client_id: clientId,
      usecase_id: llmUsecaseId,
      user_criteria_prompts: payload?.user_criteria_prompts,
      llm_judge_enabled_by_id: payload?.llm_judge_enabled_by_id,
    });
    await refreshUsecaseInfo();
  }, [clientId, llmUsecaseId, refreshUsecaseInfo]);

  useEffect(() => {
    // Fetch exactly when the version changes.
    if (selectedVersion == null) return;
    loadEvaluation(selectedVersion, currentToCompare);
    // Intentionally do NOT depend on evaluationResult to avoid refetch loops.
  }, [selectedVersion, currentToCompare, loadEvaluation]);

  const handleSelectBenchmark = useCallback((benchmarkEventId) => {
    setSelectedBenchmarkEventId(benchmarkEventId || null);
    if (selectedVersion != null) {
      loadEvaluation(selectedVersion, currentToCompare, {
        benchmarkEventId: benchmarkEventId || null,
      });
    }
  }, [selectedVersion, currentToCompare, loadEvaluation]);

  // Reset benchmark lock when the user changes the major version select.
  const prevVersionRef = useRef(selectedVersion);
  useEffect(() => {
    const prevMajor = Math.floor(Number(prevVersionRef.current));
    const nextMajor = Math.floor(Number(selectedVersion));
    if (Number.isFinite(prevMajor) && Number.isFinite(nextMajor) && prevMajor !== nextMajor) {
      setSelectedBenchmarkEventId(null);
    }
    prevVersionRef.current = selectedVersion;
  }, [selectedVersion]);

  // When teacher is still loading (handler or LLM Judge), refetch periodically so teacher card progress updates.
  const teacherInProgress =
    evaluationResult?.teacher_model_processing ||
    (evaluationResult?.teacher_model_judge_progress &&
      Number(evaluationResult.teacher_model_judge_progress.total) > 0 &&
      Number(evaluationResult.teacher_model_judge_progress.completed) < Number(evaluationResult.teacher_model_judge_progress.total));
  useEffect(() => {
    if (!teacherInProgress || selectedVersion == null) return;
    const interval = setInterval(() => {
      loadEvaluation(selectedVersion, currentToCompare, { background: true });
    }, 5000);
    return () => clearInterval(interval);
  }, [teacherInProgress, selectedVersion, currentToCompare, loadEvaluation]);

  // Keep default Compare selection aligned with usecase comparison_mode.
  // Do not overwrite explicit user selections (e.g. comparing to v12).
  useEffect(() => {
    if (!currentUsecase) return;
    const comparisonMode = Boolean(currentUsecase?.comparison_mode);

    if (!userTouchedCompareRef.current) {
      if (comparisonMode) {
        if (currentToCompare == null && teacherNameForCompare) {
          setCurrentToCompare('oldModel');
        }
      } else if (currentToCompare != null) {
        setCurrentToCompare(null);
      }
    }
  }, [currentUsecase?.comparison_mode, currentToCompare, teacherNameForCompare]);

  useEffect(() => {
    if (!teacherNameForCompare && currentToCompare === 'oldModel') {
      setCurrentToCompare(null);
    }
  }, [teacherNameForCompare, currentToCompare]);

  const compareOptions = useMemo(() => {
    return buildCompareOptions({
      teacherModelName: teacherNameForCompare,
      versions: versionOptions,
      resultsFileName: evaluationResult?.results_file_name,
    });
  }, [teacherNameForCompare, versionOptions, evaluationResult?.results_file_name]);

  const comparisonModelLabel = useMemo(() => {
    if (currentToCompare == null) return null;
    if (currentToCompare === 'oldModel') return teacherNameForCompare || teacherModelLabel;
    if (currentToCompare === 'file') return evaluationResult?.results_file_name || 'Results file';
    if (typeof currentToCompare === 'number') {
      const v = Number(currentToCompare);
      return Number.isFinite(v) && v % 1 === 0 ? `Specific AI v${v}.0` : `Specific AI v${currentToCompare}`;
    }
    return String(currentToCompare);
  }, [currentToCompare, evaluationResult?.results_file_name, teacherNameForCompare, teacherModelLabel]);

  // Same `/evaluation` payload field as non-summarization Evaluation page — required
  // so PerformanceTab can call POST /performance-metrics with the correct base_model_name.
  const {
    selectedBaseModelName,
    comparisonBaseModelName,
    comparisonModelKind,
    comparisonModelLabelForPerformance,
  } = useMemo(() => {
    const versionToBaseModel = evaluationResult?.version_to_base_model_name || {};
    const selectedMajorVersionKey =
      selectedVersion != null ? String(Math.floor(Number(selectedVersion))) : null;
    const selectedBaseModelName = selectedMajorVersionKey
      ? versionToBaseModel[selectedMajorVersionKey] || null
      : null;

    let comparisonBaseModelName = null;
    let comparisonModelLabelForPerformance = null;
    // `'oldModel'` always means the usecase teacher — performance docs
    // for teachers are stored with `model_kind="teacher"` and no infra
    // fields, so PerformanceTab must use the right query path.
    const comparisonModelKind =
      currentToCompare === 'oldModel' ? 'teacher' : 'student';
    if (typeof currentToCompare === 'number') {
      comparisonBaseModelName =
        versionToBaseModel[String(Math.floor(Number(currentToCompare)))] || null;
      comparisonModelLabelForPerformance = comparisonBaseModelName
        ? `SpecificAI v${currentToCompare}`
        : null;
    } else if (currentToCompare === 'oldModel' && teacherNameForCompare) {
      comparisonBaseModelName = teacherNameForCompare;
      comparisonModelLabelForPerformance = teacherNameForCompare;
    }
    return {
      selectedBaseModelName,
      comparisonBaseModelName,
      comparisonModelKind,
      comparisonModelLabelForPerformance,
    };
  }, [
    evaluationResult?.version_to_base_model_name,
    selectedVersion,
    currentToCompare,
    teacherNameForCompare,
  ]);

  const versionOptionsForFilterBar = useMemo(() => {
    return (versionOptions || []).map(v => ({
      label: Number(v) % 1 === 0 ? `v${Number(v)}.0` : `v${v}`,
      value: v,
    }));
  }, [versionOptions]);

  const distillationEventId = evaluationResult?.distillation_event_id || null;

  const handleGoToModel = useCallback(() => {
    if (distillationEventId) {
      setModelDetailsOpen(true);
    }
  }, [distillationEventId]);

  // Mounted in every render branch (processing, error, results) so "Go to details"
  // works even during re-evaluation, not only on the completed results view.
  const modelDetailsDrawer = (
    <ModelDetailsDrawer
      open={modelDetailsOpen}
      onClose={() => setModelDetailsOpen(false)}
      distillationEventId={distillationEventId}
      version={{
        llm_usecase_id: llmUsecaseId,
        version: selectedVersion,
        client_id: clientId,
      }}
      title={
        currentUsecase?.usecase_name
          ? `${currentUsecase.usecase_name}-v${selectedVersion || 1}.0`
          : 'Model details'
      }
      clientId={clientId}
      role={role}
      taskType={currentUsecase?.task_type || TaskType.summarization}
      selectedBenchmarkEventId={
        evaluationResult?.selected_distillation_event_id || selectedBenchmarkEventId || null
      }
      onSelectBenchmark={handleSelectBenchmark}
      drawerPush={!embeddedInTaskDrawer}
    />
  );

  const hasIncompleteTrainings = evaluationResult?.has_incomplete_trainings || false;
  const evaluationApiSuccess = Boolean(evaluationResult?.success);
  const studentModelProcessing = evaluationResult?.student_model_processing || false;

  const evaluationIsTerminal =
    evaluationResult?.distillation_status === 'completed' ||
    evaluationResult?.distillation_status === 'failed';
  const groundTruthReady = (evaluationResult?.all_metrics?.summarization_overview?.score_cards || []).some(
    (c) => c?.type === 'ground_truth' && c?.score != null && c?.score !== '',
  );
  const studentReady = (evaluationResult?.all_metrics?.summarization_overview?.score_cards || []).some(
    (c) => c?.type === 'student' && c?.score != null && c?.score !== '',
  );
  // Hold the processing overlay until BOTH benchmark and student scores are present.
  // The External File flow has no student card, so keep the fast-exit on ground-truth alone.
  const isFileComparisonEarly = currentToCompare === 'file';
  const evaluationExists =
    evaluationApiSuccess &&
    (evaluationIsTerminal ||
      (isFileComparisonEarly ? groundTruthReady : groundTruthReady && studentReady));

  // Fallback: treat the evaluation as still processing when the API response itself
  // indicates a non-terminal state, even if student_model_processing is false.
  // This prevents a blink on page refresh where WS state is empty and the only
  // source of truth is the API response.
  const apiStillProcessing = useMemo(() => {
    if (!evaluationResult) return false;
    const ds = evaluationResult.distillation_status;
    if (ds === "failed" || ds === "completed") return false;
    if (ds === "evaluating") return true;
    const cur = evaluationResult.initial_current_step;
    const tot = evaluationResult.initial_total_steps;
    return typeof cur === "number" && typeof tot === "number" && tot > 0 && cur < tot;
  }, [evaluationResult]);

  // Collect all progress entries for this usecase + major version (main and sub_task: internal, benchmark, teacher).
  const progressEntriesForVersion = useMemo(() => {
    if (!llmUsecaseId || selectedVersion == null) return [];
    const major = Math.floor(Number(selectedVersion));
    if (!Number.isFinite(major)) return [];
    const entries = [];
    for (const entry of Object.values(evaluationProgress || {})) {
      if (entry?.usecase_id !== llmUsecaseId) continue;
      const v = Number(entry?.version);
      if (!Number.isFinite(v) || Math.floor(v) !== major) continue;
      entries.push(entry);
    }
    return entries;
  }, [evaluationProgress, llmUsecaseId, selectedVersion]);

  // Single "best" entry by latest version (for backward compatibility and picking version).
  const activeEvaluationProgress = useMemo(() => {
    if (progressEntriesForVersion.length === 0) return null;
    let best = null;
    for (const entry of progressEntriesForVersion) {
      const v = Number(entry?.version);
      if (!best || v > Number(best.version)) best = entry;
    }
    return best;
  }, [progressEntriesForVersion]);

  // Merged progress for display: combine status, step_name from all entries (e.g. SpecificAI + Benchmark + Teacher).
  // Use max current_step across entries so step 7 (LLM Judge) shows as soon as any sub-task reports it,
  // without requiring a page refresh (avoids showing step 5 when only the "main" entry was last updated).
  // When we have LLM Judge sub_task entries, show: "evaluation with LLM as a Judge · SpecificAI: x/y records · Benchmark: a/b records · Teacher: p/q records".
  const mergedEvaluationProgress = useMemo(() => {
    if (progressEntriesForVersion.length === 0) return null;
    const failed = progressEntriesForVersion.find((e) => e?.status === 'failed');
    const evaluating = progressEntriesForVersion.find((e) => e?.status === 'evaluating');
    const status = failed ? 'failed' : (evaluating ? 'evaluating' : 'completed');
    const progress = Math.max(0, ...progressEntriesForVersion.map((e) => Number(e?.progress)).filter(Number.isFinite));
    const bySubTask = {};
    progressEntriesForVersion.forEach((e) => {
      const st = e?.sub_task_type;
      if (st === 'internal' || st === 'benchmark' || st === 'teacher' || st === 'external') bySubTask[st] = e;
    });
    const hasLlmJudgeSubTasks = Object.keys(bySubTask).length > 0;
    const knownTotal = (() => {
      for (const e of Object.values(bySubTask)) {
        const m = e?.step_name?.match(/(\d+)\/(\d+)/);
        if (m && m[2] !== '0') return m[2];
      }
      return '0';
    })();
    const hasExternalFile = Boolean(evaluationResult?.results_file_name);
    const usecaseExpectsTeacher = Boolean(
      currentUsecase?.comparison_mode &&
      ['teacher', 'all'].includes(currentUsecase?.comparison_mode_option),
    );
    const step_name = hasLlmJudgeSubTasks
      ? [
        'evaluation with LLM as a Judge',
        (bySubTask.internal?.step_name || `SpecificAI: 0/${knownTotal} records`),
        (bySubTask.benchmark?.step_name || `Benchmark: 0/${knownTotal} records`),
        ...((bySubTask.teacher || usecaseExpectsTeacher)
          ? [bySubTask.teacher?.step_name || `Teacher: 0/${knownTotal} records`]
          : []),
        ...((bySubTask.external || hasExternalFile) ? [bySubTask.external?.step_name || `External File: 0/${knownTotal} records`] : []),
      ].join(' · ')
      : (() => {
        const stepNames = progressEntriesForVersion
          .map((e) => e?.step_name)
          .filter((s) => typeof s === 'string' && s.trim());
        return stepNames.length > 0 ? stepNames.join(' · ') : (activeEvaluationProgress?.step_name ?? null);
      })();
    const total_steps = activeEvaluationProgress?.total_steps ?? 8;
    const stepNumbers = progressEntriesForVersion.map((e) => Number(e?.current_step)).filter(Number.isFinite);
    const current_step = stepNumbers.length > 0
      ? Math.max(0, ...stepNumbers)
      : (activeEvaluationProgress?.current_step ?? 0);
    const error_details = failed?.error_details ?? activeEvaluationProgress?.error_details;
    return {
      ...activeEvaluationProgress,
      status,
      progress: Number.isFinite(progress) ? progress : activeEvaluationProgress?.progress,
      step_name: step_name || activeEvaluationProgress?.step_name,
      total_steps,
      current_step,
      error_details,
    };
  }, [
    progressEntriesForVersion,
    activeEvaluationProgress,
    evaluationResult?.results_file_name,
    currentUsecase?.comparison_mode,
    currentUsecase?.comparison_mode_option,
  ]);

  // WS-driven overlay: stays true as long as any progress entry has non-terminal status.
  // When we receive the final "completed" (step 8) from the handler, that entry has no sub_task_type;
  // sub_task entries (internal/benchmark/teacher) may still say "evaluating". Treat step 8 completed as
  // terminal so we hide the overlay and refetch shows results without manual refresh.
  const hasCompletedStep8 = progressEntriesForVersion.some(
    (e) => e != null && e.status === 'completed' && Number(e?.current_step) >= 8
  );
  const wsInProgress =
    !hasCompletedStep8 &&
    progressEntriesForVersion.some(
      (e) => e != null && e.status !== 'completed' && e.status !== 'failed'
    );
  // If the user explicitly locked the page to a specific historical benchmark via
  // the Model Details drawer's Benchmarks tab, respect that selection and do not
  // hide the historical results behind the "Evaluating your model..." overlay
  // for an unrelated in-progress recalc on the same major version.
  const viewingHistoricalRun = Boolean(
    selectedBenchmarkEventId &&
    evaluationExists &&
    evaluationResult?.selected_distillation_event_id === selectedBenchmarkEventId &&
    evaluationResult?.is_latest_evaluation === false
  );
  const shouldShowProcessingOverlay =
    Boolean(llmUsecaseId) &&
    !viewingHistoricalRun &&
    (recalcPending || (wsInProgress && !evaluationExists));
  const shouldShowProcessing = shouldShowProcessingOverlay
    || (Boolean(llmUsecaseId) && !evaluationExists && (studentModelProcessing || apiStillProcessing))
    || (!loading && Boolean(llmUsecaseId) && !evaluationExists && (evaluationResult?.versions || []).length > 0);

  // Find the latest failed evaluation progress entry for error display in the results view.
  const latestEvaluationFailure = useMemo(() => {
    if (!llmUsecaseId || selectedVersion == null) return null;
    const major = Math.floor(Number(selectedVersion));
    if (!Number.isFinite(major)) return null;

    let latest = null;
    for (const entry of Object.values(evaluationProgress || {})) {
      if (entry?.usecase_id !== llmUsecaseId) continue;
      if (entry?.status !== "failed") continue;
      const v = Number(entry?.version);
      if (!Number.isFinite(v) || Math.floor(v) !== major) continue;
      if (!latest || v > Number(latest.version)) latest = entry;
    }
    return latest;
  }, [evaluationProgress, llmUsecaseId, selectedVersion]);

  const languageOptions = useMemo(() => {
    const byLang = evaluationResult?.all_metrics?.by_language;
    if (!byLang || typeof byLang !== "object") return [];
    return Object.keys(byLang);
  }, [evaluationResult?.all_metrics]);

  const activeEvaluationResult = useMemo(() => {
    if (!selectedLanguage) return evaluationResult;
    const byLang = evaluationResult?.all_metrics?.by_language;
    if (!byLang || typeof byLang !== "object") return evaluationResult;
    const langMetrics = byLang?.[selectedLanguage];
    if (!langMetrics) return evaluationResult;
    return { ...(evaluationResult || {}), all_metrics: langMetrics };
  }, [evaluationResult, selectedLanguage]);

  const showInitialLoading = useMemo(() => {
    if (shouldShowProcessingOverlay) return false;
    if (evaluationResult != null) return false;
    return Boolean(llmUsecaseId);
  }, [evaluationResult, llmUsecaseId, shouldShowProcessingOverlay]);

  const handleRefreshStart = useCallback(() => {
    if (!llmUsecaseId || selectedVersion == null) {
      setRecalcPending(true);
      return;
    }
    recalcStartedAtRef.current = Date.now();

    // Remove stale progress entries for this usecase+major so the processing overlay
    // never briefly shows the previous run's terminal state (e.g. step 6/6).
    const major = Math.floor(Number(selectedVersion));
    if (setEvaluationProgress) {
      setEvaluationProgress((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          const e = next[k];
          if (e?.usecase_id !== llmUsecaseId) continue;
          const v = Number(e?.version);
          if (Number.isFinite(v) && Math.floor(v) === major) {
            delete next[k];
          }
        }
        return next;
      });
    }

    setRecalcRun(null);
    setRecalcPending(true);
    loadEvaluation(selectedVersion, currentToCompare);
  }, [llmUsecaseId, loadEvaluation, selectedVersion, currentToCompare, setEvaluationProgress]);

  const handleRefreshComplete = useCallback((result) => {
    if (!result?.success) {
      setRecalcPending(false);
      return;
    }
    // A realtime resync looks this run up when every update for it was missed.
    setRecalcRun(recalculationRunFromResponse(result.response));
  }, []);

  // Poll evaluation while teacher LLM-as-Judge is in progress (no teacher score yet)
  // so the overview can show loading and "X out of Y processed" on hover.
  useEffect(() => {
    if (activeTab !== 'overview' || !showComparison || !evaluationResult?.success) return;
    const cards = evaluationResult?.all_metrics?.summarization_overview?.score_cards || [];
    const teacherCard = cards.find((c) => c?.type === 'teacher');
    if (!teacherCard) return;
    const hasTeacherScore =
      teacherCard.score != null && teacherCard.score !== '' && Number.isFinite(Number(teacherCard.score));
    if (hasTeacherScore) return;

    const intervalMs = 10000;
    const t = setInterval(() => {
      loadEvaluation(selectedVersion, currentToCompare, { background: true });
    }, intervalMs);
    return () => clearInterval(t);
  }, [
    activeTab,
    showComparison,
    evaluationResult?.success,
    evaluationResult?.all_metrics?.summarization_overview?.score_cards,
    selectedVersion,
    currentToCompare,
    loadEvaluation,
  ]);

  // Clear recalcPending once the real WS progress reaches a terminal state,
  // or after a safety timeout (the wsInProgress flag in shouldShowProcessingOverlay
  // keeps the overlay visible independently of recalcPending).
  useEffect(() => {
    if (!recalcPending) return;

    const status = activeEvaluationProgress?.status;
    if (status === 'completed' || status === 'failed') {
      setRecalcPending(false);
      return;
    }

    const startedAt = recalcStartedAtRef.current;
    if (startedAt && Date.now() - startedAt > 30_000) {
      setRecalcPending(false);
    }
  }, [recalcPending, activeEvaluationProgress]);

  if (showInitialLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '295px',
        }}
      >
        <Spinner />
      </div>
    );
  }

  if (shouldShowProcessing) {
    const progressEntry = mergedEvaluationProgress ?? activeEvaluationProgress;

    // When a recalculation was just triggered, use clean initial values instead of
    // stale ones from the previous completed run (which might show step 6/6).
    const isNewRecalc = recalcPending && (!progressEntry || progressEntry.status === "completed");
    const initialProgress = isNewRecalc ? 0 : (evaluationResult?.initial_progress || 0);
    const initialStepName = isNewRecalc ? "Preparing evaluation dataset" : (evaluationResult?.initial_step_name || "Setting up evaluation environment");
    const initialTotalSteps = evaluationResult?.initial_total_steps || 8;
    const initialCurrentStep = isNewRecalc ? 1 : (evaluationResult?.initial_current_step || 1);

    return (
      <div>
        <div
          className="fs-unmask"
          style={{
            borderRadius: 8,
          }}
        >
          <EvaluationViewTabs activeTab={activeTab} onChange={setActiveTab} items={EVAL_VIEW_ITEMS}>
          <FilterBar
            compareOptions={compareOptions}
            versionOptions={versionOptionsForFilterBar}
            versionOptionsRaw={versionOptions}
            selectedVersion={selectedVersion}
            setSelectedVersion={setSelectedVersion}
            currentToCompare={currentToCompare}
            setCurrentToCompare={(next) => {
              userTouchedCompareRef.current = true;
              setCurrentToCompare(next ?? null);
            }}
            selectedLabel={null}
            setSelectedLabel={() => { }}
            languageOptions={languageOptions}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            getClassOptions={() => []}
            dropdownLabelRender={() => null}
            localUsecase={currentUsecase}
            viewMode="table"
            setViewMode={() => { }}
            moveToScreen={moveToScreen}
            showImproveEvals={true}
            improveButtonLabel="Improve Results"
            hideLabelSelect={true}
            hideViewToggle={true}
            taskType={currentUsecase?.task_type || TaskType.summarization}
            onRefreshStart={handleRefreshStart}
            onRefreshComplete={handleRefreshComplete}
            evaluationProgress={activeEvaluationProgress}
            onRetrain={onRetrain}
            distillationEventId={distillationEventId}
            onGoToModel={distillationEventId ? handleGoToModel : undefined}
          />
          {(progressEntry?.status === "failed"
            || (!progressEntry && evaluationResult?.distillation_status === "failed")
          ) ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ width: '100%' }}>
                <AlertBanner
                  feedback="error"
                  showIcon
                  text={`Evaluation stopped — ${
                    progressEntry?.error_details
                    || evaluationResult?.distillation_error_details
                    || "We hit an error while evaluating this version. Please choose another version to view results."
                  }`}
                  showButton={false}
                  showCloseButton={false}
                />
              </div>
            </div>
          ) : (
            <EvaluationProcessingState
              progress={progressEntry?.progress}
              status={progressEntry?.status}
              stepName={progressEntry?.step_name}
              totalSteps={progressEntry?.total_steps}
              currentStep={progressEntry?.current_step}
              errorDetails={
                isNewRecalc
                  ? null
                  : progressEntry
                    ? progressEntry.error_details
                    : evaluationResult?.distillation_error_details
              }
              initialProgress={initialProgress}
              initialStepName={initialStepName}
              initialTotalSteps={initialTotalSteps}
              initialCurrentStep={initialCurrentStep}
            />
          )}
          </EvaluationViewTabs>
        </div>
        {modelDetailsDrawer}
      </div>
    );
  }

  // NOTE: Don't use hooks after early returns (rules-of-hooks).
  const showEmptyState = (() => {
    if (loading) return false;
    if (!evaluationResult) return false;
    const versions = evaluationResult?.versions || [];
    if (versions.length === 0) return true;
    return false;
  })();

  // When the API returned an error but there are versions available (e.g. a
  // recalculation failed), show error + FilterBar so the user can switch version.
  const showErrorWithVersionSelector = !loading && evaluationResult && !evaluationResult.success
    && (evaluationResult?.versions || []).length > 0;

  if (showEmptyState && !showErrorWithVersionSelector) {
    // No evaluated version yet: "Crunching the numbers" while a training is
    // still running for this usecase, otherwise the plain empty state.
    const trainingInProgress = isTrainingInProgress({
      trainStatuses,
      usecaseId: llmUsecaseId,
      usecaseStatus: currentUsecase?.status,
      distillationEventId: evaluationResult?.distillation_event_id || null,
      evaluationResult,
    });
    return trainingInProgress ? (
      <EvaluationTrainingInProgressState />
    ) : (
      <EvaluationEmptyState
        outcome={evaluationResult?.last_training_outcome ?? null}
        onRetrain={onRetrain}
      />
    );
  }

  if (showErrorWithVersionSelector) {
    return (
      <div>
        <div
          className="fs-unmask"
          style={{ borderRadius: 8 }}
        >
          <EvaluationViewTabs activeTab={activeTab} onChange={setActiveTab} items={EVAL_VIEW_ITEMS}>
          <div>
            <FilterBar
              compareOptions={compareOptions}
              versionOptions={versionOptionsForFilterBar}
              versionOptionsRaw={versionOptions}
              selectedVersion={selectedVersion}
              setSelectedVersion={setSelectedVersion}
              currentToCompare={currentToCompare}
              setCurrentToCompare={(next) => {
                userTouchedCompareRef.current = true;
                setCurrentToCompare(next ?? null);
              }}
              selectedLabel={null}
              setSelectedLabel={() => { }}
              languageOptions={languageOptions}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              getClassOptions={() => []}
              dropdownLabelRender={() => null}
              localUsecase={currentUsecase}
              viewMode="table"
              setViewMode={() => { }}
              moveToScreen={moveToScreen}
              showImproveEvals={true}
              improveButtonLabel="Improve Results"
              hideLabelSelect={true}
              hideViewToggle={true}
              taskType={currentUsecase?.task_type || TaskType.summarization}
              onRefreshStart={handleRefreshStart}
              onRefreshComplete={handleRefreshComplete}
            evaluationProgress={activeEvaluationProgress}
            onRetrain={onRetrain}
              distillationEventId={distillationEventId}
              onGoToModel={distillationEventId ? handleGoToModel : undefined}
            />
          </div>
          <div style={{ marginTop: '1rem' }}>
            <AlertBanner
              feedback="error"
              showIcon
              text={`Evaluation error — ${
                evaluationResult?.distillation_error_details
                || evaluationResult?.user_info
                || "An error occurred while loading evaluation results. Please try selecting a different version."
              }`}
              showButton={false}
              showCloseButton={false}
            />
          </div>
          </EvaluationViewTabs>
        </div>
        {modelDetailsDrawer}
      </div>
    );
  }

  return (
    <div>
      <div
        className="fs-unmask"
        style={{
          borderRadius: 8,
        }}
      >
        <EvaluationViewTabs activeTab={activeTab} onChange={setActiveTab} items={EVAL_VIEW_ITEMS}>
        <IncompleteTrainingAlert show={hasIncompleteTrainings} version={currentUsecase?.version} style={{ marginBottom: '1rem' }} />
        {!failureAlertDismissed && (latestEvaluationFailure || evaluationResult?.distillation_status === "failed" || evaluationResult?.distillation_error_details) && (
          <div style={{ marginBottom: '1rem' }}>
            <AlertBanner
              feedback="error"
              showIcon
              text={`${latestEvaluationFailure?.step_name || "Evaluation error"} — ${
                latestEvaluationFailure?.error_details
                || evaluationResult?.distillation_error_details
                || "We hit an error while evaluating this version. Please choose another version to view results."
              }`}
              showButton={false}
              showCloseButton
              onCloseClick={() => setFailureAlertDismissed(true)}
            />
          </div>
        )}

        {activeTab !== 'performance' && (
          <EvaluationErrorsAlert
            evaluationErrors={evaluationResult?.evaluation_errors || null}
            teacherEvaluationErrors={evaluationResult?.teacher_evaluation_errors || null}
            currentVersion={`v${selectedVersion}`}
            teacherModelName={teacherNameForCompare}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Filters row (Overview only) */}
        {activeTab === 'overview' && (
          <div>
            <FilterBar
              compareOptions={compareOptions}
              versionOptions={versionOptionsForFilterBar}
              versionOptionsRaw={versionOptions}
              selectedVersion={selectedVersion}
              setSelectedVersion={setSelectedVersion}
              currentToCompare={currentToCompare}
              setCurrentToCompare={(next) => {
                userTouchedCompareRef.current = true;
                setCurrentToCompare(next ?? null);
              }}
              selectedLabel={null}
              setSelectedLabel={() => { }}
              languageOptions={languageOptions}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              getClassOptions={() => []}
              dropdownLabelRender={() => null}
              localUsecase={currentUsecase}
              viewMode="table"
              setViewMode={() => { }}
              moveToScreen={moveToScreen}
              showImproveEvals={true}
              improveButtonLabel="Improve Results"
              hideLabelSelect={true}
              hideViewToggle={true}
              taskType={currentUsecase?.task_type || TaskType.summarization}
              onRefreshStart={handleRefreshStart}
              onRefreshComplete={handleRefreshComplete}
            evaluationProgress={activeEvaluationProgress}
            onRetrain={onRetrain}
              distillationEventId={distillationEventId}
              onGoToModel={distillationEventId ? handleGoToModel : undefined}
              runDateIso={evaluationResult?.selected_event_created_datetime}
              isLatestEvaluation={evaluationResult?.is_latest_evaluation}
              runStatusVersion={evaluationResult?.selected_version ?? selectedVersion}
              onSwitchToLatest={() => handleSelectBenchmark(null)}
            />
          </div>
        )}

        {activeTab === 'overview' && (
          <>
            <SummarizationOverview
              evaluationResult={activeEvaluationResult}
              loading={loading}
              showComparison={showComparison}
              usecase={usecaseInfo}
              onPersistJudgeConfig={persistJudgeConfig}
              isFileComparison={currentToCompare === 'file'}
              taskType={currentUsecase?.task_type || TaskType.summarization}
            />
          </>
        )}
        {activeTab === 'samples' && (
          <SummarizationSamples
            clientId={clientId}
            currentUsecase={currentUsecase}
            classicMetricsConfig={
              usecaseInfo?.classic_metrics_config ?? currentUsecase?.classic_metrics_config
            }
            llmJudgeEnabledById={
              usecaseInfo?.llm_judge_enabled_by_id ?? currentUsecase?.llm_judge_enabled_by_id
            }
            llmUsecaseId={llmUsecaseId}
            version={selectedVersion}
            setVersion={setSelectedVersion}
            versionOptions={versionOptions}
            compareOptions={compareOptions}
            compareTarget={currentToCompare}
            comparisonModelLabel={comparisonModelLabel}
            studentModelLabel={studentModelLabel}
            selectedLanguage={selectedLanguage}
            selectedDistillationEventId={selectedBenchmarkEventId}
          />
        )}
        {activeTab === 'performance' && (
          <div style={{ marginTop: 24 }}>
            <PerformanceTab
              baseModelName={selectedBaseModelName}
              benchmarkNames={evaluationResult?.benchmarks}
              clientId={clientId}
              comparisonBaseModelName={comparisonBaseModelName}
              comparisonModelKind={comparisonModelKind}
              comparisonModelLabel={comparisonModelLabelForPerformance}
              costConfig={performanceCostConfig}
              currentModelLabel={studentModelLabel || `Specific AI v${selectedVersion}`}
              filters={performanceFilters}
              llmUsecaseId={llmUsecaseId}
              modelVersion={selectedVersion}
              onCostConfigChange={setPerformanceCostConfig}
              onFiltersChange={setPerformanceFilters}
              taskType={currentUsecase?.task_type || TaskType.summarization}
            />
          </div>
        )}
        </EvaluationViewTabs>
      </div>
      {modelDetailsDrawer}
    </div>
  );
};

export default SummarizationEvaluation;

